/**
 * GUMSHOE — monologue queue. Serializes 'monologue:play' so beats never
 * overlap: one active beat at a time, the rest wait their turn. AUDIO emits
 * 'monologue:done'; a fallback timer keeps the queue moving if it never does.
 * Owned by GAME.
 */
import type { EventBus, MonologueBeat } from '../core/types'

/** Extra slack past durationMs before we assume AUDIO went quiet on us. */
const FALLBACK_GRACE_MS = 1500

export interface MonologueQueue {
  /** Add a beat; plays immediately if nothing is active. Duplicate ids already queued are dropped. */
  enqueue(beat: MonologueBeat): void
  /** Call from the 'monologue:done' handler. */
  notifyDone(beatId: string): void
}

export const createMonologueQueue = (bus: EventBus): MonologueQueue => {
  let pending: readonly MonologueBeat[] = []
  let active: MonologueBeat | null = null
  let fallbackTimer: ReturnType<typeof setTimeout> | null = null

  const clearFallback = (): void => {
    if (fallbackTimer !== null) {
      clearTimeout(fallbackTimer)
      fallbackTimer = null
    }
  }

  const playNext = (): void => {
    if (active !== null) return
    const next = pending[0]
    if (next === undefined) return
    pending = pending.slice(1)
    active = next
    fallbackTimer = setTimeout(
      () => finish(next.id),
      next.durationMs + FALLBACK_GRACE_MS
    )
    bus.emit('monologue:play', { beat: next })
  }

  const finish = (beatId: string): void => {
    if (active === null || active.id !== beatId) return
    clearFallback()
    active = null
    playNext()
  }

  const enqueue = (beatToQueue: MonologueBeat): void => {
    const isAlreadyQueued =
      active?.id === beatToQueue.id || pending.some((b) => b.id === beatToQueue.id)
    if (isAlreadyQueued) return
    pending = [...pending, beatToQueue]
    playNext()
  }

  return { enqueue, notifyDone: finish }
}
